import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import {
    Alert,
    AlertIcon,
    Box,
    Button,
    DarkMode,
    IconButton,
    Text,
    useMediaQuery
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { FaPlayCircle, FaVolumeUp } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';
import ReactPlayer from 'react-player/lazy';

import { EventType } from '~/components/Event';
import { useLivestreamData } from '~/components/Event/provider/LivestreamDataProvider';
import { hasOndemandData, isPlayableSchedule } from '~/components/EventSchedules/Playlist';
import Timeline, { TimelineScroller } from '~/components/EventSchedules/Timeline';
import FullLoader from '~/components/FullLoader';
import { EventScheduleFieldsFragment } from '~/graphql/hasura';
import { useAnalytics, useWatchTimeAnalytics } from '~/lib/analytics';
import {
    generateTreeSchedule,
    getParentOndemandSource
} from '~/lib/data-transform/event-schedules';

import AdaptivePlayerLayout, { AdaptivePlayerLayoutHandle } from './AdaptivePlayerLayout';

const OndemandPlayerLight: FunctionComponent<{ event: EventType }> = ({ event }) => {
    const [isTimelineInline] = useMediaQuery(['(min-width: 600px)', '(min-aspect-ratio: 16/9)']);
    const playerRef = useRef<ReactPlayer>(null);
    const adaptivePlayerLayoutRef = useRef<AdaptivePlayerLayoutHandle>(null);
    const scheduleScrollRef = useRef<HTMLDivElement>(null);
    const schedulesTree = generateTreeSchedule(event.event_schedules);
    const [playerVolume] = useState<number>(1);
    const router = useRouter();
    const { program_id } = router.query;
    const [isPlaying, setPlaying] = useState(Boolean(program_id));
    const [playerMuted, setPlayerMuted] = useState(true);
    const [url, setUrl] = useState<string>();
    const [activeSchedule, setActiveSchedule] = useState<EventScheduleFieldsFragment>();
    const { eventAccess } = useLivestreamData();

    const { analytics } = useAnalytics();

    const sendWatchTimeAnalytics = useWatchTimeAnalytics({
        _category: 'ondemand',
        _action: 'watch_time',
        event_id: event.id,
        event_schedule_id: activeSchedule?.id
    });

    useEffect(() => {
        if (!eventAccess.canViewOnDemand()) {
            return;
        }
        if (program_id) {
            event.event_schedules.map((schedule) => {
                if (
                    schedule.id === program_id &&
                    eventAccess.canViewSchedule(schedule, 'ondemand')
                ) {
                    setActiveSchedule(schedule);
                }
            });
        } else {
            const firstOnDemandSchedule = event.event_schedules.filter((schedule) => {
                return (
                    isPlayableSchedule(schedule) &&
                    hasOndemandData(schedule) &&
                    eventAccess.canViewSchedule(schedule, 'ondemand')
                );
            })?.[0];

            if (firstOnDemandSchedule) {
                router.push(
                    `/events/${event.id}/on-demand?program_id=${firstOnDemandSchedule.id}`,
                    undefined,
                    {
                        shallow: true,
                        locale: router.locale
                    }
                );
            }
        }
    }, [program_id, eventAccess]);

    useEffect(() => {
        if (!eventAccess.canViewOnDemand()) {
            return;
        }
        if (activeSchedule && playerRef) {
            if (eventAccess.canViewSchedule(activeSchedule, 'ondemand')) {
                if (activeSchedule.ondemand_source?.src) {
                    setUrl(activeSchedule.ondemand_source?.src);
                } else if (activeSchedule.ondemand_seconds) {
                    const parentOnDemandSource = getParentOndemandSource(
                        event.event_schedules,
                        activeSchedule
                    );

                    if (parentOnDemandSource?.src && url !== parentOnDemandSource.src) {
                        setUrl(parentOnDemandSource.src);
                    }

                    playerRef.current?.seekTo(activeSchedule.ondemand_seconds);
                }
            } else {
                setUrl('');
            }
        }
    }, [activeSchedule, playerRef, url, eventAccess]);

    /**
     * Called when media is loaded and ready to play. If playing is set to true, media will play immediately
     */
    function onReady() {
        if (activeSchedule && activeSchedule.ondemand_seconds) {
            playerRef.current?.seekTo(activeSchedule.ondemand_seconds);
        }
    }

    /**
     * Called when media starts or resumes playing after pausing or buffering
     */
    function onPlay() {
        setPlaying(true);

        if (adaptivePlayerLayoutRef.current) {
            adaptivePlayerLayoutRef.current.scrollToMain();
        }

        if (activeSchedule && eventAccess.canViewSchedule(activeSchedule, 'ondemand')) {
            analytics?.send('player_play', {
                _category: 'ondemand',
                _action: 'play',
                event_id: event.id,
                event_schedule_id: activeSchedule.id
            });

            sendWatchTimeAnalytics('start');
        }
    }

    /**
     * Callback containing played and loaded progress as a fraction, and playedSeconds and loadedSeconds in seconds
     *
     *  eg { played: 0.12, playedSeconds: 11.3, loaded: 0.34, loadedSeconds: 16.7 }
     */
    function onProgress() {
        if (activeSchedule && eventAccess.canViewSchedule(activeSchedule, 'ondemand')) {
            sendWatchTimeAnalytics('progress');
        }
    }

    /**
     * Called when media is paused
     */
    function onPause() {
        sendWatchTimeAnalytics('stop');
    }

    /**
     * Called when media finishes playing (Does not fire when loop is set to true)
     */
    function onEnded() {
        sendWatchTimeAnalytics('stop');
    }

    /**
     * Called when media starts buffering
     */
    function onBuffer() {
        sendWatchTimeAnalytics('stop');
    }

    function play() {
        setPlayerMuted(false);
        setPlaying(true);
    }

    if (eventAccess.isLoading()) {
        return <FullLoader />;
    }

    if (!eventAccess.canViewOnDemand()) {
        return (
            <Box maxW={800} mx="auto" p={10} textAlign="left">
                <Alert status="warning">
                    <AlertIcon />
                    <Text>
                        <FormattedMessage
                            id="landingpage.ondemand.no_access"
                            defaultMessage="Sie haben für <strong>{event_title}</strong>{br}keine On-Demand Videos gebucht."
                            values={{
                                event_title: event.title
                            }}
                        />
                    </Text>
                </Alert>
            </Box>
        );
    }

    return (
        <AdaptivePlayerLayout
            fixedOnScroll={isTimelineInline ? false : true}
            ref={adaptivePlayerLayoutRef}
        >
            <Box h="full" w="full">
                <ReactPlayer
                    ref={playerRef}
                    volume={playerVolume}
                    onReady={onReady}
                    onPlay={onPlay}
                    onProgress={onProgress}
                    onPause={onPause}
                    onEnded={onEnded}
                    onBuffer={onBuffer}
                    url={url}
                    muted={playerMuted}
                    playing={isPlaying}
                    playsinline
                    controls={true}
                    loop={false}
                    width="100%"
                    height="100%"
                    style={{
                        backgroundColor: 'black',
                        margin: '0px auto'
                    }}
                />
                <DarkMode>
                    <Box
                        position="absolute"
                        zIndex={9999}
                        top="48%"
                        left={'50%'}
                        transform="translateX(-50%)"
                    >
                        {playerMuted && isPlaying ? (
                            <Button
                                boxShadow="lg"
                                onClick={play}
                                size="md"
                                variant="solid"
                                borderRadius="full"
                                colorScheme="red"
                                aria-label="Ton ein"
                                leftIcon={<FaVolumeUp size="24px" />}
                            >
                                Ton einschalten
                            </Button>
                        ) : null}
                        {!isPlaying ? (
                            <IconButton
                                boxShadow="lg"
                                onClick={() => setPlaying(true)}
                                width="80px"
                                height="80px"
                                variant="solid"
                                borderRadius="full"
                                colorScheme="brand"
                                aria-label="Play / Pause"
                                icon={<FaPlayCircle size="60px" />}
                            />
                        ) : null}
                    </Box>
                </DarkMode>
            </Box>
            <TimelineScroller schedules={schedulesTree} event={event}>
                <Timeline
                    event={event}
                    scrollContainer={scheduleScrollRef}
                    schedules={schedulesTree}
                    options={{ showTime: false, showStreamPresence: false }}
                    isInline={isTimelineInline}
                    onLectureClick={(es) => {
                        sendWatchTimeAnalytics('stop');

                        router.push(
                            `/events/${event.id}/on-demand?program_id=${es.id}`,
                            undefined,
                            {
                                shallow: true,
                                locale: router.locale
                            }
                        );
                    }}
                    context="ondemand"
                />
            </TimelineScroller>
        </AdaptivePlayerLayout>
    );
};

export default OndemandPlayerLight;
